/**
 * Shared LoRa Types
 *
 * Common types for Meshtastic and MeshCore integration.
 *
 * ⚠️ EXPERIMENTAL: This integration is under active development.
 */

// ============= Device Types =============

/**
 * LoRa backend type - which protocol to use
 */
export type LoRaBackend = 'meshtastic' | 'meshcore'

/**
 * Unified LoRa connection status
 */
export type LoRaConnectionStatus =
  | 'disconnected'
  | 'scanning'
  | 'connecting'
  | 'connected'
  | 'configuring'
  | 'ready'
  | 'error'

/**
 * Unified LoRa device info
 */
export interface LoRaDevice {
  id: string
  name: string
  backend: LoRaBackend
  firmwareVersion: string
  batteryLevel: number
  hasGps: boolean
  region: string
  lastSeen: Date
  isConnected: boolean
}

// ============= Message Types =============

/**
 * LoRa message types
 * Extended for Twin Cities Rapid Response features
 */
export type LoRaMessageType =
  | 'text'           // 0x01 - Text message
  | 'state_sync'     // 0x02 - CRDT state sync
  | 'poi'            // 0x03 - POI report
  | 'ping'           // 0x04 - Ping
  | 'pong'           // 0x05 - Pong
  | 'vehicle'        // 0x10 - Vehicle sighting
  | 'plate_lookup'   // 0x11 - Plate lookup request
  | 'plate_response' // 0x12 - Plate lookup response
  | 'handoff_req'    // 0x13 - Handoff request
  | 'handoff_ack'    // 0x14 - Handoff accept

/**
 * CT02 binary format metadata
 */
export interface CT02Metadata {
  /** Raw flags byte */
  flags: number
  /** Is the payload LZ4 compressed */
  isCompressed: boolean
  /** Is this message a fragment */
  isFragmented: boolean
  /** Does this message have a signature */
  isSigned: boolean
}

/**
 * Unified LoRa message for CrowdTalkie integration
 */
export interface LoRaMessage {
  /** Unique message ID */
  id: string
  /** Message type */
  type: LoRaMessageType
  /** CrowdTalkie room ID */
  roomId: string
  /** Message payload (encrypted) */
  payload: string
  /** Ed25519 signature */
  signature?: string
  /** Sender peer ID */
  senderId?: string
  /** Timestamp */
  timestamp: number
  /** Source backend */
  backend: LoRaBackend
  /** Signal info */
  rssi?: number
  snr?: number
  /** Hop count */
  hopCount?: number
  /** CT02 binary format metadata (if decoded from CT02) */
  _ct02?: CT02Metadata
}

/**
 * Outgoing message to send via LoRa
 */
export interface LoRaOutgoingMessage {
  roomId: string
  type: LoRaMessageType
  payload: string
  signature?: string
  senderId?: string
}

// ============= Vehicle Message Types =============

/**
 * Vehicle sighting for LoRa transmission (compact format)
 */
export interface LoRaVehicleSighting {
  /** License plate (if available) */
  p?: string
  /** Vehicle type: 0=sedan, 1=suv, 2=van, 3=truck, 4=unmarked, 5=other */
  v?: number
  /** Color index */
  c?: number
  /** Direction: 0-7 for N/NE/E/SE/S/SW/W/NW, 8=stationary */
  d?: number
  /** Is following */
  f?: boolean
  /** Latitude (fixed point, 6 decimals) */
  la: number
  /** Longitude (fixed point, 6 decimals) */
  lo: number
  /** Description (truncated to 50 chars) */
  de?: string
}

/**
 * Plate lookup request for LoRa
 */
export interface LoRaPlateLookup {
  /** Plate to look up */
  p: string
  /** Request ID for response matching */
  r: string
}

/**
 * Plate lookup response for LoRa
 */
export interface LoRaPlateResponse {
  /** Request ID */
  r: string
  /** Plate */
  p: string
  /** Status: 0=unknown, 1=confirmed_hostile, 2=suspected, 3=cleared */
  s: number
  /** Sighting count */
  c: number
}

/**
 * Handoff request for LoRa
 */
export interface LoRaHandoffRequest {
  /** Request ID */
  r: string
  /** Vehicle sighting */
  v: LoRaVehicleSighting
  /** From zone name */
  z: string
  /** Street name */
  st?: string
}

// ============= Channel Types =============

/**
 * Unified channel mapping for both backends
 */
export interface LoRaChannelMapping {
  roomId: string
  roomSecret: string
  channelKey: Uint8Array
  channelName: string
  backend: LoRaBackend
  /** Meshtastic channel index */
  meshtasticChannelIndex?: number
  createdAt: Date
}

// ============= Event Types =============

/**
 * Unified event handlers for LoRa
 */
export interface LoRaEventHandlers {
  onStatusChange?: (status: LoRaConnectionStatus, backend: LoRaBackend) => void
  onDeviceInfo?: (device: LoRaDevice) => void
  onMessageReceived?: (message: LoRaMessage) => void
  onError?: (error: Error, backend: LoRaBackend) => void
  onDisconnect?: (backend: LoRaBackend) => void
}

// ============= Configuration Types =============

/**
 * LoRa configuration
 */
export interface LoRaConfig {
  /** Preferred backend (auto-select if not specified) */
  preferredBackend?: LoRaBackend
  /** Auto-reconnect on disconnect */
  autoReconnect: boolean
  /** Maximum reconnect attempts */
  maxReconnectAttempts: number
  /** Heartbeat interval in ms */
  heartbeatIntervalMs: number
  /** Enable debug logging */
  debug: boolean
}

/**
 * Default LoRa configuration
 */
export const DEFAULT_LORA_CONFIG: LoRaConfig = {
  preferredBackend: undefined,
  autoReconnect: true,
  maxReconnectAttempts: 5,
  heartbeatIntervalMs: 30000,
  debug: false,
}

// ============= Storage Types =============

/**
 * Persisted LoRa settings
 */
export interface LoRaSettings {
  /** Last used backend */
  lastBackend?: LoRaBackend
  /** Last connected device ID */
  lastDeviceId?: string
  /** Last connected device name */
  lastDeviceName?: string
  /** Channel mappings by room ID */
  channelMappings: Record<string, LoRaChannelMapping>
  /** Configuration overrides */
  config: Partial<LoRaConfig>
}

/**
 * Default settings
 */
export const DEFAULT_LORA_SETTINGS: LoRaSettings = {
  channelMappings: {},
  config: {},
}

// ============= Bridge Types =============

/**
 * Bridge state for room integration
 */
export interface LoRaBridgeState {
  /** Is the bridge active for this room */
  isActive: boolean
  /** Current backend being used */
  backend: LoRaBackend | null
  /** Channel mapping for this room */
  channelMapping: LoRaChannelMapping | null
  /** Messages received via LoRa */
  receivedMessageIds: Set<string>
  /** Last broadcast timestamp for rate limiting */
  lastBroadcastTime: number
}

/**
 * Bridge options for room hooks
 */
export interface LoRaBridgeOptions {
  roomId: string
  roomSecret: string
  roomName?: string
  enabled: boolean
}
